import { useCallback, useContext, useMemo } from 'react'
import { match } from 'react-router-dom'
import { IRouteCodes, IRoute } from 'interfaces'
import useCore from './useCore'
import { RouteContext } from 'components/Provider/RouteProvider'

interface IRoutesHook {
  mainRoute: IRoute
  route: IRoute
  subRoutes: IRouteCodes
  defaultRoutePath: string
  mainRoutePath: string
  match: match
}
export function useRoutes() {
  const { entity } = useCore()
  const { match } = useContext(RouteContext)
  const currentPath = match.path || ''

  const mainRoute = useMemo(() => {
    const entityPath = entity.path.replace('/', '')
    const resourcePath = currentPath
      .split('/')
      .filter((item) => item && entityPath !== item)
      .shift()

    if (!resourcePath) {
      return {} as IRoute
    }

    const routeKey =
      Object.keys(entity.route).find((key) => entity.route[key].path.endsWith(resourcePath)) || ''
    const children = entity.route[routeKey]

    return children || ({} as IRoute)
  }, [entity, currentPath])

  const getRoute = useCallback(
    (routes: IRouteCodes, currentPath: string): IRoute => {
      const entityPath = entity.path.replace('/', '')
      const paths = currentPath.split('/').filter((item) => item && entityPath !== item)

      const routeKey = Object.keys(routes).find(
        (key) => routes[key].path.endsWith(paths[0]) || routes[key].path.endsWith(paths.join('/'))
      )
      const children = routes[routeKey || '']

      if (paths.length > 1 && children && !children.routes) {
        return children
      }

      if (paths.length > 1 && children && children.routes) {
        paths.shift()
        const subRoutes = getRoute(children.routes, paths.join('/'))
        return subRoutes
      }

      if (children && children.routes) {
        const currentRouteKey = Object.keys(children.routes).find(
          (key) => children.routes && children.routes[key].path === ''
        )
        if (currentRouteKey) return children.routes[currentRouteKey]
      }
      return children
    },
    [entity.path]
  )

  const getSubRoutes = useCallback(
    (routes: IRouteCodes, currentPath: string): IRouteCodes => {
      const paths = (currentPath.split('/') || []).filter(
        (obj) => !!obj && !entity.path.includes(obj)
      )
      const routeKey = Object.keys(routes).find((key) => routes[key].path.endsWith(paths[0])) || ''
      const children = routes[routeKey]

      if (!children || !children.routes) {
        return {} as IRouteCodes
      }

      if (paths.length > 1) {
        paths.shift()
        const subRoutes = getSubRoutes(children.routes, paths.join('/'))
        return subRoutes
      }

      return children.routes
    },
    [entity.path]
  )

  const getDefaultRoute = useCallback(
    (routes: IRouteCodes) => {
      const defaultRouteKey = Object.keys(routes).find((key) => routes[key].defaultRoute)
      if (!defaultRouteKey) {
        return {} as IRoute
      }
      return `${entity.path}${routes[defaultRouteKey].path}`
    },
    [entity.path]
  )

  return {
    mainRoute,
    route: getRoute(entity.route, currentPath),
    subRoutes: getSubRoutes(entity.route, currentPath),
    defaultRoutePath: getDefaultRoute(entity.route),
    mainRoutePath: `${entity.path}${mainRoute.path || ''}`,
    match,
  } as IRoutesHook
}

export default useRoutes
